import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, CheckboxV2 } from "@resideo/blueprint-react";
import type { DeviceStatus } from "@/api/services/manageDevicesServices";
import styles from "./ManageDevicesFilterPanel.module.css";

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface DeviceFilterValues {
  statuses: string[];
  deviceTypes: string[];
  locations: string[];
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface ManageDevicesFilterPanelProps {
  open: boolean;
  /** Currently applied filter values (used to seed the draft when opened). */
  value: DeviceFilterValues;
  /** Device-type options with display labels (e.g. { label: "Security Panel", value: "SecurityPanel" }). */
  deviceTypeOptions: FilterOption[];
  /** Location names fetched from the API. */
  locationOptions: string[];
  onApply: (next: DeviceFilterValues) => void;
  onClose: () => void;
}

const STATUS_VALUES: DeviceStatus[] = ["online", "offline"];

const EMPTY: DeviceFilterValues = {
  statuses: [],
  deviceTypes: [],
  locations: [],
};

const toggle = (list: string[], value: string): string[] =>
  list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];

export const ManageDevicesFilterPanel = ({
  open,
  value,
  deviceTypeOptions,
  locationOptions,
  onApply,
  onClose,
}: ManageDevicesFilterPanelProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DeviceFilterValues>(value);

  const statusOptions: FilterOption[] = STATUS_VALUES.map((v) => ({
    label: t(`manageDevices.statusOptions.${v}`),
    value: v,
  }));

  // Re-seed the draft from the applied values every time the panel opens so
  // that cancelling discards any unsaved edits.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  // Close on Escape while the panel is open.
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const renderGroup = (
    title: string,
    options: FilterOption[],
    key: keyof DeviceFilterValues
  ) => (
    <fieldset className={styles.group}>
      <legend className={styles.groupTitle}>{title}</legend>
      {options.length === 0 ? (
        <p className={styles.emptyOption}>{t("manageDevices.noOptions")}</p>
      ) : (
        options.map((option) => (
          <label key={option.value} className={styles.option}>
            <CheckboxV2
              checked={draft[key].includes(option.value)}
              onChange={() =>
                setDraft((prev) => ({
                  ...prev,
                  [key]: toggle(prev[key], option.value),
                }))
              }
            />
            <span className={styles.optionLabel}>{option.label}</span>
          </label>
        ))
      )}
    </fieldset>
  );

  return (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <aside
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={t("manageDevices.ariaLabels.filterDevices")}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 className={styles.heading}>{t("manageDevices.filterHeading")}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("manageDevices.ariaLabels.closeFilterPanel")}
          >
            <CloseIcon />
          </button>
        </header>

        <div className={styles.body}>
          {renderGroup(t("manageDevices.groups.status"), statusOptions, "statuses")}
          {renderGroup(
            t("manageDevices.groups.deviceType"),
            deviceTypeOptions,
            "deviceTypes"
          )}
          {renderGroup(
            t("manageDevices.groups.location"),
            locationOptions.map((name) => ({ label: name, value: name })),
            "locations"
          )}
        </div>

        <footer className={styles.footer}>
          <Button
            variant="secondary"
            className={styles.clearButton}
            onClick={() => setDraft(EMPTY)}
          >
            {t("manageDevices.filterClearAll")}
          </Button>
          <Button
            variant="secondary"
            className={styles.cancelButton}
            onClick={onClose}
          >
            {t("manageDevices.cancel")}
          </Button>
          <Button
            variant="primary"
            className={styles.applyButton}
            onClick={() => onApply(draft)}
          >
            {t("manageDevices.apply")}
          </Button>
        </footer>
      </aside>
    </div>
  );
};

export default ManageDevicesFilterPanel;
